// @file Store for class information
import { captureFetchException } from '@@/bits/error_tracker'
import { ClassInformation as ClassInformationApi } from '@@/dashboard/padlet_api'
import { HttpCode } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import type { ClassInformation as ClassInformationType } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export const useClassInformationStore = defineStore('classInformationStore', () => {
  const globalSnackbarStore = useGlobalSnackbarStore()

  const classInformation = ref<string>('')
  const isLoadingClassInformation = ref(false)

  const fetchClassInformation = async (): Promise<void> => {
    isLoadingClassInformation.value = true
    try {
      const response = await ClassInformationApi.fetchClassInformation()
      classInformation.value = (response.data as JsonAPIResource<ClassInformationType>).attributes.value
    } catch (error) {
      // 404 is expected when user hasn't set class information yet - don't track as error
      if (error.status !== HttpCode.NotFound) {
        captureFetchException(error, { source: 'ClassInformationStore' })
      }
    } finally {
      isLoadingClassInformation.value = false
    }
  }

  const upsertClassInformation = async ({ newClassInfo }: { newClassInfo: string }): Promise<void> => {
    const trimmedClassInfo = newClassInfo?.trim()
    isLoadingClassInformation.value = true

    try {
      await ClassInformationApi.upsert(trimmedClassInfo)
      classInformation.value = trimmedClassInfo
    } catch (e) {
      globalSnackbarStore.genericFetchError()
      captureFetchException(e, { source: 'ClassInformationUpsertion' })
    } finally {
      isLoadingClassInformation.value = false
    }
  }

  return {
    classInformation,
    isLoadingClassInformation,
    fetchClassInformation,
    upsertClassInformation,
  }
})
